import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ItineraryDay,
  StopCreate,
  StopReorder,
  StopUpdate,
} from '@travel-planner/shared';
import { api } from '../api/client';

export const itineraryKey = (tripId: string) => ['itinerary', tripId] as const;

export function useItinerary(tripId: string | null) {
  return useQuery<ItineraryDay[]>({
    queryKey: tripId ? itineraryKey(tripId) : ['itinerary', 'none'],
    queryFn: () => api.get<ItineraryDay[]>(`/trips/${tripId}/itinerary`),
    enabled: Boolean(tripId),
  });
}

export function useAddStop(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StopCreate) => api.post(`/trips/${tripId}/itinerary/stops`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: itineraryKey(tripId) }),
  });
}

export function useUpdateStop(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stopId, patch }: { stopId: string; patch: StopUpdate }) =>
      api.patch(`/trips/${tripId}/itinerary/stops/${stopId}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: itineraryKey(tripId) }),
  });
}

export function useDeleteStop(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stopId: string) => api.del<void>(`/trips/${tripId}/itinerary/stops/${stopId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: itineraryKey(tripId) }),
  });
}

export function useReorderStops(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StopReorder) => api.put(`/trips/${tripId}/itinerary/reorder`, input),
    onSettled: () => qc.invalidateQueries({ queryKey: itineraryKey(tripId) }),
  });
}
